import type {
  AnalyticsEventAction,
  AnalyticsEventActionType,
  DeviceType,
  FilteredWidgetContext,
  VisitorContext,
  WidgetContext,
  WidgetState,
} from '@/domain/analytics';
import { PAGE_URL_EVENTS, WIDGET_STATES } from '@/domain/analytics';
import type { AnalyticsEvent } from '@/domain/analyticsEvent';
import { SCHEMA_VERSION, SOURCE } from '@/domain/analyticsEvent';
import type { Platform } from '@/domain/deviceConfig';
import type { DisplayMode } from '@/domain/ui';
import { getAnalyticsPageUrl } from '@/lib/analyticsPageUrl';
import { getBootstrapClosedState } from '@/lib/bootstrapState';
import generateId from '@/lib/generateId';
import { isMobileDevice } from '@/lib/isMobileDevice';
import { logger } from '@/lib/logger';

export function getDeviceType(platform: Platform): DeviceType {
  if (platform === 'IOS' || platform === 'ANDROID') {
    return 'mobile';
  }

  return isMobileDevice() ? 'mobile' : 'desktop';
}

export function getWidgetContext({
  appId,
  displayMode,
  legacyUI,
  platform,
}: {
  appId: string;
  displayMode: DisplayMode;
  legacyUI: boolean;
  platform: Platform;
}): WidgetContext {
  return {
    appId,
    deviceType: getDeviceType(platform),
    legacyUI,
    pageUrl: getAnalyticsPageUrl(),
    platform,
    widgetState: toWidgetState(displayMode),
  };
}

export function getVisitorContext({
  clientId,
  gladlyCustomerId,
}: {
  clientId: string;
  gladlyCustomerId?: string;
}): VisitorContext {
  return {
    clientId,
    ...(gladlyCustomerId && { gladlyCustomerId }),
  };
}

export function newAnalyticsEvent(orgId: string, eventAction: AnalyticsEventAction): AnalyticsEvent {
  const {
    action,
    appId,
    clientId,
    deviceType,
    gladlyCustomerId,
    legacyUI,
    pageUrl,
    platform,
    widgetState,
    initiator,
    ...value
  } = eventAction;
  const now = new Date().toISOString();

  return {
    id: generateId(),
    org_id: orgId,
    action: {
      type: action,
      value,
    },
    context: {
      widget: toFilteredWidgetContext(action, { appId, deviceType, legacyUI, pageUrl, platform, widgetState }),
      visitor: getVisitorContext({ clientId, gladlyCustomerId }),
    },
    initiator,
    created_at: now,
    source: SOURCE,
    schema_version: SCHEMA_VERSION,
  };
}

/**
 * Widget context for one event, carrying `pageUrl` only for the types in
 * {@link PAGE_URL_EVENTS}, and only when one was resolved. Omits the key rather than
 * setting it to undefined, so the field is absent rather than present-and-empty.
 */
function toFilteredWidgetContext(action: AnalyticsEventActionType, ctx: WidgetContext): FilteredWidgetContext {
  if (PAGE_URL_EVENTS.has(action)) {
    if (ctx.pageUrl) {
      return ctx;
    }

    logger.warn('[analytics] no pageUrl resolved for an event that carries one', { action });
  }

  const { pageUrl: _pageUrl, ...rest } = ctx;
  return rest;
}

export function toWidgetState(displayMode: DisplayMode): WidgetState {
  switch (displayMode) {
    case 'Fullscreen':
      return WIDGET_STATES.MAXIMIZED;
    case 'Panel':
      return WIDGET_STATES.SIDEBAR;
    case 'Minimized':
      return getBootstrapClosedState() ? WIDGET_STATES.BUBBLE : WIDGET_STATES.DEFAULT;
  }
}
